import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import VendorSpendPill from "@/components/VendorSpendPill";
import LandlordQuickCard from "@/components/LandlordQuickCard";
import {
  Buildings, House, Wrench, Package, ShieldCheck, Warning, ClockCounterClockwise,
  ChartBar, ArrowLeft, MapPin, ClipboardText, Sparkle,
} from "@phosphor-icons/react";

/**
 * PropertySnapshot — executive glance of building health.
 * Aggregates work orders, unit occupancy, asset health, warranty status,
 * predictive-maintenance flags, PM notes, and recurring problem categories.
 * Route: /admin/properties/:address/snapshot
 */
export default function PropertySnapshot() {
  const { address: encoded } = useParams();
  const address = decodeURIComponent(encoded || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!address) return;
    setLoading(true);
    api.get(`/properties/snapshot?address=${encodeURIComponent(address)}`)
      .then(r => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to load snapshot")))
      .finally(() => setLoading(false));
  }, [address]);

  if (loading) return <AdminShell><div className="p-8 font-bold" data-testid="snapshot-loading">Loading snapshot…</div></AdminShell>;
  if (!data) return <AdminShell><div className="p-8 font-bold text-red-700" data-testid="snapshot-error">Snapshot unavailable.</div></AdminShell>;

  const u = data.units || {};
  const wo = data.work_orders || {};
  const a = data.assets || {};
  const byStatus = u.by_status || {};
  const assetByStatus = a.by_status || {};
  const totalUnits = u.total || 0;
  const occupancyPct = totalUnits > 0 ? Math.round(((byStatus.occupied || 0) / totalUnits) * 100) : null;
  const backHref = `/admin/properties/${encodeURIComponent(data.address || address)}`;

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="property-snapshot-page">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
          <Link to={backHref} data-testid="snapshot-back-link" className="hover:text-[#0f172a] inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Back to property
          </Link>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <ChartBar weight="fill" size={30} className="text-fuchsia-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight" data-testid="snapshot-title">
              Property Snapshot
            </h1>
            <div className="text-sm text-slate-700 font-medium mt-1 flex items-center gap-1">
              <MapPin size={14}/> {data.address}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {totalUnits > 3 && (
              <Link to={`/admin/properties/${encodeURIComponent(data.address || address)}/board`}
                data-testid="open-multi-unit-board"
                className="h-9 px-3 border-2 border-cyan-800 bg-cyan-600 hover:bg-cyan-500 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <Buildings size={12} weight="fill"/> Multi-Unit Board
              </Link>
            )}
            {data.landlord_id && (
              <LandlordQuickCard landlordId={data.landlord_id} label={data.landlord_name || "landlord"}
                className="h-9 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"/>
            )}
            <VendorSpendPill address={data.address || address} compact/>
          </div>
        </div>

        {/* HEADLINE KPI ROW */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Units" value={totalUnits} sub={occupancyPct != null ? `${occupancyPct}% occupied` : ""} testid="snap-kpi-units"/>
          <Kpi label="Open WOs" value={wo.open || 0} accent={(wo.open || 0) > 0 ? "text-amber-600" : "text-slate-500"} testid="snap-kpi-open"/>
          <Kpi label="Closed 30d" value={wo.completed_30d || 0} sub={wo.avg_turnaround_hours != null ? `${wo.avg_turnaround_hours}h avg` : ""} testid="snap-kpi-closed"/>
          <Kpi label="Replace Flags" value={a.replace_recommended || 0} accent={(a.replace_recommended || 0) > 0 ? "text-red-600" : "text-slate-500"} testid="snap-kpi-replace"/>
        </div>

        {/* OCCUPANCY BREAKDOWN */}
        <div className="mt-4 rugged-card p-5 bg-white" data-testid="snap-occupancy-card">
          <div className="flex items-center gap-2 mb-3">
            <Buildings weight="bold" size={18} className="text-fuchsia-600"/>
            <h2 className="font-display font-black text-sm uppercase tracking-wider">Occupancy</h2>
            {u.ring_secured > 0 && (
              <span className="ml-auto px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">
                ◉ {u.ring_secured} Ring-secured
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatusPill label="Occupied" value={byStatus.occupied || 0} color="emerald" testid="snap-occ-occupied"/>
            <StatusPill label="Vacant" value={byStatus.vacant || 0} color="amber" testid="snap-occ-vacant"/>
            <StatusPill label="Tenant Pulled" value={byStatus.tenant_pulled || 0} color="red" testid="snap-occ-pulled"/>
            <StatusPill label="Unfit" value={byStatus.unfit || 0} color="slate" testid="snap-occ-unfit"/>
          </div>
          {totalUnits === 0 && (
            <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              No unit rows on file for this address yet
            </div>
          )}
        </div>

        {/* ASSET HEALTH */}
        <div className="mt-4 rugged-card p-5 bg-white" data-testid="snap-assets-card">
          <div className="flex items-center gap-2 mb-3">
            <Package weight="bold" size={18} className="text-purple-600"/>
            <h2 className="font-display font-black text-sm uppercase tracking-wider">Asset Health</h2>
            <span className="ml-auto text-xs text-slate-500 font-bold">{a.total || 0} tracked</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatusPill label="Operational" value={assetByStatus.operational || 0} color="emerald" testid="snap-asset-op"/>
            <StatusPill label="Broken" value={assetByStatus.broken || 0} color="red" testid="snap-asset-broken"/>
            <StatusPill label="Maintenance" value={assetByStatus.maintenance || 0} color="amber" testid="snap-asset-maint"/>
            <StatusPill label="Retired" value={assetByStatus.retired || 0} color="slate" testid="snap-asset-retired"/>
          </div>

          {(a.replace_recommended || 0) > 0 && (
            <div className="mt-3 p-3 border-l-4 border-red-500 bg-red-50" data-testid="snap-replace-callout">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-800">
                ⚠︎ Predictive Maintenance
              </div>
              <div className="mt-1 text-sm text-red-900">
                <b>{a.replace_recommended}</b> asset{a.replace_recommended === 1 ? "" : "s"} showing 3+ tickets in 6 months — recommend replacement over repair.
              </div>
              <Link to={`/admin/assets?address_key=${encodeURIComponent(data.normalized_address)}`} data-testid="snap-replace-link"
                className="mt-2 inline-block text-[10px] font-black uppercase tracking-widest text-red-700 underline">
                Review flagged assets →
              </Link>
            </div>
          )}
        </div>

        {/* WARRANTY */}
        <div className="mt-4 rugged-card p-5 bg-white" data-testid="snap-warranty-card">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck weight="fill" size={20} className="text-emerald-600"/>
            <h2 className="font-display font-black text-sm uppercase tracking-wider">Warranty Coverage</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StatusPill label="Active" value={a.warranty_active || 0} color="emerald" testid="snap-war-active"/>
            <StatusPill label="Expiring &lt;60d" value={a.warranty_expiring_soon || 0} color="amber" testid="snap-war-expiring"/>
            <StatusPill label="Expired" value={a.warranty_expired || 0} color="slate" testid="snap-war-expired"/>
          </div>

          {(a.expiring_list || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Next to expire</div>
              <div className="space-y-1.5">
                {a.expiring_list.map(item => {
                  const soon = (item.days_left ?? 9999) <= 60;
                  return (
                    <Link key={item.id} to={`/admin/assets?open=${item.id}`}
                      data-testid={`snap-warranty-item-${item.id}`}
                      className={`block p-2 border-2 hover:border-[#0f172a] transition-colors ${soon ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-black text-sm truncate">{item.name}</div>
                        <div className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${soon ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"}`}>
                          {item.days_left}d left
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5">
                        {item.location && <span className="mr-2">{item.location}</span>}
                        {item.provider && <span className="mr-2">· {item.provider}</span>}
                        {item.warranty_end && <span>· ends {item.warranty_end}</span>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {(a.total || 0) === 0 && (
            <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
              No assets on file yet. Add appliances to unlock warranty tracking.
            </div>
          )}
        </div>

        {/* RECURRING PROBLEMS */}
        {(data.recurring_categories || []).length > 0 && (
          <div className="mt-4 rugged-card p-5 bg-white border-l-4 border-amber-500" data-testid="snap-recurring-card">
            <div className="flex items-center gap-2 mb-3">
              <Warning weight="bold" size={18} className="text-amber-600"/>
              <h2 className="font-display font-black text-sm uppercase tracking-wider">Recurring Problems (6mo)</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.recurring_categories.map(r => (
                <span key={r.category}
                  data-testid={`snap-recurring-${r.category}`}
                  className="px-2 py-1 text-[10px] font-black uppercase tracking-wider bg-white border-2 border-amber-500 text-amber-800">
                  {r.category} × {r.count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* PM NOTES + MAINTENANCE */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rugged-card p-5 bg-white" data-testid="snap-pmnotes-card">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardText weight="bold" size={18} className="text-cyan-700"/>
              <h2 className="font-display font-black text-sm uppercase tracking-wider">PM Notes</h2>
            </div>
            <div className="text-3xl font-display font-black text-cyan-800">{data.pm_notes?.count || 0}</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">
              {data.pm_notes?.last_at ? `Last logged ${new Date(data.pm_notes.last_at).toLocaleDateString()}` : "None yet"}
            </div>
          </div>

          <div className="rugged-card p-5 bg-white" data-testid="snap-maint-card">
            <div className="flex items-center gap-2 mb-2">
              <Wrench weight="bold" size={18} className="text-amber-700"/>
              <h2 className="font-display font-black text-sm uppercase tracking-wider">Maintenance Info</h2>
            </div>
            {data.maintenance?.filter_size || data.maintenance?.filter_type || data.maintenance?.hvac_last_serviced ? (
              <div className="text-xs space-y-1">
                {data.maintenance.filter_size && <div><b>Filter:</b> {data.maintenance.filter_size} {data.maintenance.filter_type ? `· ${data.maintenance.filter_type}` : ""}</div>}
                {data.maintenance.hvac_last_serviced && <div><b>HVAC last serviced:</b> {data.maintenance.hvac_last_serviced}</div>}
              </div>
            ) : (
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Not set</div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Link to={backHref} data-testid="snap-footer-back"
            className="h-10 px-4 inline-flex items-center gap-1 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-slate-100">
            <ArrowLeft size={12}/> Full property view
          </Link>
          <Link to={`/admin/assets?address_key=${encodeURIComponent(data.normalized_address)}`}
            data-testid="snap-footer-assets"
            className="h-10 px-4 inline-flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
            <Package size={12}/> Manage assets
          </Link>
          <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-400">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function Kpi({ label, value, sub = "", accent = "text-slate-900", testid }) {
  return (
    <div className="rugged-card p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-black text-3xl leading-none ${accent}`}>{value}</div>
      {sub && <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400">{sub}</div>}
    </div>
  );
}

function StatusPill({ label, value, color = "emerald", testid }) {
  const map = {
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-800",
    amber: "border-amber-500 bg-amber-50 text-amber-800",
    red: "border-red-500 bg-red-50 text-red-800",
    slate: "border-slate-400 bg-slate-100 text-slate-700",
  };
  return (
    <div className={`p-3 border-2 ${map[color] || map.slate}`} data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest">{label}</div>
      <div className="mt-0.5 font-display font-black text-2xl leading-none">{value}</div>
    </div>
  );
}
